/**
 * Emulation helpers — read/write the SAME localStorage keys the Vue ERP uses
 * (RW-ERP-Service/src/pages/app/products/draftproducts/components/UserEmulation.vue),
 * so an emulation started in either app is observed by the other.
 */

import { EMULATION_FLAG, STORAGE_KEYS } from '../constants/storageKeys';
import type { FetchUserResponseData } from '../api/users.api';
import type { VueEmulationState } from '../config/probe-vue-session';
import {
  syncEndEmulationToVueApp,
  syncStartEmulationToVueApp,
} from '../config/cross-origin-sync';
import { VUE_CACHE_STORES, clearVueCache } from './vueLocalCache';

export interface EmulationState {
  active: boolean;
  emulatedUserId: string | null;
  emulatedUserName: string | null;
}

const safeWrite = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Quota exceeded or storage unavailable — emulation start will fail gracefully.
  }
};

const safeRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* noop */
  }
};

const safeRead = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const readEmulationState = (): EmulationState => {
  if (typeof window === 'undefined') {
    return { active: false, emulatedUserId: null, emulatedUserName: null };
  }
  const flag = window.localStorage.getItem(STORAGE_KEYS.isEmulate);
  return {
    active: flag === EMULATION_FLAG.ON,
    emulatedUserId: window.localStorage.getItem(STORAGE_KEYS.emulatedUserId),
    emulatedUserName: window.localStorage.getItem(STORAGE_KEYS.emulatedUserName),
  };
};

export interface EmulatedIdentity {
  email: string;
  name: string;
}

/**
 * The active emulated user's email + display name, or `null` when not
 * emulating (or the persisted detail is missing/malformed).
 *
 * `readEmulationState()` alone is not enough for this: it carries
 * `emulatedUserName` but no email, because nothing needed the emulated
 * user's email until now. The email lives in `emulatedUserDetail` — the
 * full `FetchUserResponseData` persisted by `beginEmulation()` (see
 * `src/api/users.api.ts`'s `userDetails.email`).
 */
export const readEmulatedIdentity = (): EmulatedIdentity | null => {
  const { active, emulatedUserName } = readEmulationState();
  if (!active) return null;

  const raw = safeRead(STORAGE_KEYS.emulatedUserDetail);
  if (!raw) return null;

  try {
    // TWO levels of `data`, matching beginEmulation's `JSON.stringify({ data:
    // response })`, where `response` (FetchUserResponseData) is itself
    // `{ data: { userDetails } }` — the whole point being to mirror the full
    // axios response object the way Vue stores it. Parsing only one level
    // here made `email` always undefined, so this always returned null and
    // silently fell back to the REAL signed-in user's email during emulation
    // — the button-visibility bug where an emulated preparer couldn't act on
    // their own assigned accounts even though their ROLE resolved correctly
    // (role comes from `usePermissions()`, a separate path unaffected by
    // this).
    const parsed = JSON.parse(raw) as {
      data?: { data?: { userDetails?: { email?: unknown; displayName?: unknown } } };
    };
    const email = parsed.data?.data?.userDetails?.email;
    if (typeof email !== 'string' || !email) return null;

    const displayName = parsed.data?.data?.userDetails?.displayName;
    const fallbackName =
      typeof displayName === 'string' && displayName ? displayName : email;
    // Deliberately not `??` — a blank (but present) emulatedUserName must
    // still fall back, not display as an empty name.
    const name =
      emulatedUserName && emulatedUserName.trim() !== '' ? emulatedUserName : fallbackName;
    return { email, name };
  } catch {
    return null;
  }
};

/**
 * Persist the emulated user's identity AND mirror it into the Vue ERP so an
 * emulation started in React is observed by Vue (the two apps are different
 * origins and do not share localStorage). Returns the resolved displayName so
 * the caller can show it without re-reading storage.
 *
 * Awaits the cross-origin sync iframe before resolving so the caller can
 * reload only after Vue has been told — the reload would otherwise tear down
 * the iframe mid-flight. A fresh React-initiated emulation also clears any
 * stale dismissal marker (see `STORAGE_KEYS.emulationDismissedVueUserId`), so a
 * previously-ended emulation can be re-started without being suppressed.
 */
export const beginEmulation = async (
  response: FetchUserResponseData,
): Promise<{
  userId: string;
  displayName: string;
}> => {
  const userDetails = response.data.userDetails;
  // Vue stores the *full* axios response (`JSON.stringify(data)` where `data`
  // is the response object). We mirror by stringifying the wrapper that
  // contains `data.data.userDetails`, matching the Vue read path.
  const detailJson = JSON.stringify({ data: response });

  safeWrite(STORAGE_KEYS.isEmulate, EMULATION_FLAG.ON);
  safeWrite(STORAGE_KEYS.emulatedUserId, userDetails.userId);
  safeWrite(STORAGE_KEYS.emulatedUserName, userDetails.displayName);
  safeWrite(STORAGE_KEYS.emulatedUserDetail, detailJson);
  // Starting a new emulation invalidates any prior "just-ended" suppression.
  safeRemove(STORAGE_KEYS.emulationDismissedVueUserId);

  // Mirror into Vue's origin. Best-effort: if the iframe can't reach Vue the
  // local emulation still stands (same one-directional caveat as logout).
  await syncStartEmulationToVueApp(
    userDetails.userId,
    userDetails.displayName,
    detailJson,
  );

  return { userId: userDetails.userId, displayName: userDetails.displayName };
};

/**
 * Mirror a Vue-origin emulation into THIS origin's localStorage so the
 * emulated identity carries across the Vue → React navigation (the two apps
 * are different origins and do not share localStorage). Called with the
 * `emulation` block returned by `probeVueSession()`.
 *
 * Returns `true` when it actually changed local storage (the caller should
 * then reload so `PermissionContext` / `useCurrentUserId` rebuild against the
 * emulated user — `resolveUserId()` already swaps to `emulatedUserId` when
 * `isEmulate === 'true'`).
 *
 * Deliberately one-directional: it only APPLIES an active Vue emulation, and
 * is a no-op when Vue is not emulating. It never clears a local emulation off
 * an inactive Vue probe — React-started emulation is not mirrored to Vue, so
 * auto-clearing would wipe a React-initiated session on the next probe. The
 * same-user short-circuit also prevents a reload loop (after the reload the
 * local `emulatedUserId` already matches, so the next probe is a no-op).
 */
export const applyVueEmulationIfActive = (
  state: VueEmulationState | null,
): boolean => {
  const dismissedId = safeRead(STORAGE_KEYS.emulationDismissedVueUserId);

  if (!state || !state.active || !state.emulatedUserId) {
    // Vue is not emulating. Any dismissal marker has done its job (the
    // just-ended emulation is gone on Vue too) — clear it so a future
    // Vue-started emulation for the same user isn't wrongly suppressed.
    if (dismissedId) safeRemove(STORAGE_KEYS.emulationDismissedVueUserId);
    return false;
  }

  // A React-side "end emulation" recorded this exact user as just-dismissed.
  // Suppress re-mirroring it so the ended emulation doesn't bounce back before
  // Vue's own clear (via `end-emulation.html`) is reflected. Keep the marker:
  // Vue may still report this user on the next probe until its clear lands.
  if (dismissedId && dismissedId === state.emulatedUserId) {
    return false;
  }

  // Vue is emulating a DIFFERENT user than the one we dismissed — the marker
  // is stale, drop it and mirror the new emulation.
  if (dismissedId) safeRemove(STORAGE_KEYS.emulationDismissedVueUserId);

  const current = readEmulationState();
  if (current.active && current.emulatedUserId === state.emulatedUserId) {
    return false;
  }

  safeWrite(STORAGE_KEYS.isEmulate, EMULATION_FLAG.ON);
  safeWrite(STORAGE_KEYS.emulatedUserId, state.emulatedUserId);
  if (state.emulatedUserName) {
    safeWrite(STORAGE_KEYS.emulatedUserName, state.emulatedUserName);
  }
  // Stored verbatim — React never parses this for identity (that comes from
  // `emulatedUserId` via `resolveUserId`), so Vue's JSON nesting is fine.
  if (state.emulatedUserDetail) {
    safeWrite(STORAGE_KEYS.emulatedUserDetail, state.emulatedUserDetail);
  }
  return true;
};

/**
 * Clear all emulation flags AND the emulated permission cache. The page
 * MUST be reloaded by the caller (Vue ERP's `pageReload()` pattern in
 * `UserEmulation.vue:183-186`) so the permission/cache layer rebuilds
 * against the real user.
 *
 * The IDB `emulatedPermission` store wipe mirrors Vue's
 * `clearEmulatedPermissionCache()` call in `UserEmulation.vue:251-253`.
 * Without it, the next emulation session would resurrect the prior
 * emulated user's snapshot from IDB.
 */
export const endEmulation = async (): Promise<void> => {
  // Record the just-ended emulated user so the post-reload / next-focus probe
  // does not resurrect it before Vue's own clear is reflected. Read BEFORE we
  // wipe the local keys. Only set when there is an id to suppress.
  const endingUserId = safeRead(STORAGE_KEYS.emulatedUserId);
  if (endingUserId) {
    safeWrite(STORAGE_KEYS.emulationDismissedVueUserId, endingUserId);
  }

  safeWrite(STORAGE_KEYS.isEmulate, EMULATION_FLAG.OFF);
  safeRemove(STORAGE_KEYS.emulatedUserId);
  safeRemove(STORAGE_KEYS.emulatedUserName);
  safeRemove(STORAGE_KEYS.emulatedUserDetail);
  safeRemove(STORAGE_KEYS.emulatedUserPermission);
  // Wipe the dedicated React-side localStorage mirror for the emulated
  // permission snapshot (separate from `STORAGE_KEYS.emulatedUserPermission`
  // which is the Vue mirror key — both must be cleared so cross-app
  // hydration on next emulation isn't tainted).
  safeRemove(STORAGE_KEYS.permissionSnapshotEmulated);
  try {
    await clearVueCache(VUE_CACHE_STORES.emulatedPermission);
  } catch {
    /* noop — logout will also run deleteAllDatabases */
  }

  // Clear Vue's emulation too (cross-origin). Best-effort: if the page is
  // missing / unreachable the local dismissal marker still prevents the bounce
  // on this origin. Awaited so the caller can reload only after the sync.
  await syncEndEmulationToVueApp();
};

/**
 * Called by the probe handler in `AuthContext` whenever the probe returns.
 * Detects the case where Vue confirmed it is no longer emulating while this
 * origin is still in emulation mode — whether the emulation was started from
 * Vue or from React — and clears the local emulation state to match.
 *
 * Returns `true` when local emulation was cleared (caller should reload).
 * Returns `false` when nothing needed clearing:
 *   – `emulation` is null: the probe failed (Vue unreachable / timeout) — we
 *     cannot distinguish "Vue ended emulation" from "probe timed out", so we
 *     leave the local state unchanged, or
 *   – Vue is still emulating (nothing to clear), or
 *   – This origin is already not emulating (already in sync).
 *
 * No cross-origin sync is performed: Vue already cleared its side. No
 * dismissal marker is written: Vue's state is authoritative and the next
 * probe will also return inactive, so there is no bounce risk.
 */
export const clearVueEndedEmulation = (
  emulation: VueEmulationState | null,
): boolean => {
  // null == probe failed — we don't know Vue's state, leave local state alone.
  if (!emulation) return false;
  if (emulation.active) return false;
  if (safeRead(STORAGE_KEYS.isEmulate) !== EMULATION_FLAG.ON) return false;

  safeWrite(STORAGE_KEYS.isEmulate, EMULATION_FLAG.OFF);
  safeRemove(STORAGE_KEYS.emulatedUserId);
  safeRemove(STORAGE_KEYS.emulatedUserName);
  safeRemove(STORAGE_KEYS.emulatedUserDetail);
  safeRemove(STORAGE_KEYS.emulatedUserPermission);
  safeRemove(STORAGE_KEYS.permissionSnapshotEmulated);
  void clearVueCache(VUE_CACHE_STORES.emulatedPermission).catch(() => {});
  return true;
};
